import React, { useState, useEffect, useCallback } from 'react';
import type { RaceEvent, Category, EventDocumentStatusResponse } from '../../types';
import { raceService } from '../../services/raceService';
import { adminService } from '../../services/adminService';
import { eventService } from '../../services/eventService';
import type { EventDocumentType } from '../../services/eventService';
import EventHelpModal from '../../components/EventHelpModal';

/**
 * RaceEventManager — Admin component for creating, editing, and managing race events.
 *
 * Requirement 15.2 — Race event creation requires event name, year, date, and Race_Category.
 * Requirement 15.3 — Upload race-specific documents (Notice of Race, Sailing Instructions).
 */

const RaceEventManager: React.FC = () => {
  // Race events list
  const [events, setEvents] = useState<RaceEvent[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form visibility state
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Form state
  const [editingEvent, setEditingEvent] = useState<RaceEvent | null>(null);
  const [formName, setFormName] = useState('');
  const [formYear, setFormYear] = useState('');
  const [formDate, setFormDate] = useState('');
  const [formCategory, setFormCategory] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Delete confirmation state
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Document upload state
  const [uploadRaceId, setUploadRaceId] = useState<string | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadDocType, setUploadDocType] = useState<EventDocumentType>('notice_of_race');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);

  // Document delete state
  const [deletingDocType, setDeletingDocType] = useState<string | null>(null);

  // Event Help Modal state
  const [helpModalRaceId, setHelpModalRaceId] = useState<string | null>(null);
  const [helpModalEventName, setHelpModalEventName] = useState<string>('');

  // Document status state
  const [documentStatus, setDocumentStatus] = useState<EventDocumentStatusResponse | null>(null);
  const [loadingDocStatus, setLoadingDocStatus] = useState(false);
  const [pollingStatus, setPollingStatus] = useState(false);

  /** Check if any document is still processing. */
  const isAnyDocumentProcessing = useCallback((status: EventDocumentStatusResponse | null): boolean => {
    if (!status) return false;
    if (status.noticeOfRace?.extractionStatus === 'processing') return true;
    if (status.sailingInstructions?.extractionStatus === 'processing') return true;
    if (status.amendments.some(a => a.extractionStatus === 'processing')) return true;
    return false;
  }, []);

  /** Poll document status while processing. */
  useEffect(() => {
    if (!uploadRaceId || !pollingStatus) return;

    const pollInterval = setInterval(async () => {
      try {
        const status = await eventService.getEventDocumentStatus(uploadRaceId);
        setDocumentStatus(status);
        
        // Stop polling when no documents are processing
        if (!isAnyDocumentProcessing(status)) {
          setPollingStatus(false);
        }
      } catch {
        // Continue polling on error
      }
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(pollInterval);
  }, [uploadRaceId, pollingStatus, isAnyDocumentProcessing]);

  /** Fetch race events and categories on mount. */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [eventsData, categoriesData] = await Promise.all([
        raceService.getRaces(),
        adminService.getCategories(),
      ]);
      setEvents(eventsData);
      setCategories(categoriesData);
    } catch {
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /** Validate the form fields. Returns true if valid. */
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formName.trim()) {
      errors.name = 'Event name is required';
    }
    if (!formYear.trim()) {
      errors.year = 'Year is required';
    } else if (isNaN(Number(formYear)) || Number(formYear) < 2000 || Number(formYear) > 2100) {
      errors.year = 'Year must be a valid number between 2000 and 2100';
    }
    if (!formDate.trim()) {
      errors.date = 'Event date is required';
    }
    if (!formCategory) {
      errors.category = 'Category is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /** Handle form submission for create or edit. */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage(null);
    setError(null);

    if (!validateForm()) return;

    setSubmitting(true);
    try {
      if (editingEvent) {
        await adminService.updateRaceEvent(editingEvent.raceId, {
          name: formName.trim(),
          year: Number(formYear),
          date: formDate,
          category: formCategory,
        });
        setSuccessMessage('Race event updated successfully.');
      } else {
        await adminService.createRaceEvent({
          name: formName.trim(),
          year: Number(formYear),
          date: formDate,
          category: formCategory,
        });
        setSuccessMessage('Race event created successfully.');
      }
      resetForm();
      await fetchData();
    } catch {
      setError(editingEvent ? 'Failed to update race event.' : 'Failed to create race event.');
    } finally {
      setSubmitting(false);
    }
  };

  /** Populate form for editing an existing event. */
  const handleEditClick = (event: RaceEvent) => {
    setEditingEvent(event);
    setFormName(event.name);
    setFormYear(String(event.year));
    setFormDate(event.date || '');
    setFormCategory(event.category);
    setFormErrors({});
    setSuccessMessage(null);
    setError(null);
    setShowCreateForm(true);  // Show form when editing
  };

  /** Reset form to create mode. */
  const resetForm = () => {
    setEditingEvent(null);
    setFormName('');
    setFormYear('');
    setFormDate('');
    setFormCategory('');
    setFormErrors({});
    setShowCreateForm(false);  // Hide form after reset
  };

  /** Handle delete confirmation. */
  const handleDeleteClick = (raceId: string) => {
    setDeleteConfirmId(raceId);
  };

  /** Confirm and execute delete. */
  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;

    setDeleting(true);
    setError(null);
    try {
      await adminService.deleteRaceEvent(deleteConfirmId);
      setSuccessMessage('Race event deleted successfully.');
      setDeleteConfirmId(null);
      await fetchData();
    } catch {
      setError('Failed to delete race event.');
    } finally {
      setDeleting(false);
    }
  };

  /** Cancel delete. */
  const handleDeleteCancel = () => {
    setDeleteConfirmId(null);
  };

  /** Handle document upload. */
  const handleDocumentUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadRaceId || !uploadFile) return;

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);
    try {
      await eventService.uploadEventDocument(uploadRaceId, uploadFile, uploadDocType);
      setUploadSuccess(`Document uploaded successfully. Extraction in progress...`);
      setUploadFile(null);
      // Start polling for status updates
      setPollingStatus(true);
      // Refresh document status
      fetchDocumentStatus(uploadRaceId);
    } catch {
      setUploadError('Failed to upload document.');
    } finally {
      setUploading(false);
    }
  };

  /** Handle document delete. */
  const handleDeleteDocument = async (documentType: string) => {
    if (!uploadRaceId) return;
    
    if (!window.confirm(`Are you sure you want to delete the ${documentType.replace(/_/g, ' ')}? This will also remove all extracted sections.`)) {
      return;
    }

    setDeletingDocType(documentType);
    setUploadError(null);
    setUploadSuccess(null);
    try {
      await eventService.deleteEventDocument(uploadRaceId, documentType);
      setUploadSuccess(`Document deleted successfully.`);
      // Refresh document status
      fetchDocumentStatus(uploadRaceId);
    } catch {
      setUploadError('Failed to delete document.');
    } finally {
      setDeletingDocType(null);
    }
  };

  /** Fetch document status for a race event. */
  const fetchDocumentStatus = useCallback(async (raceId: string) => {
    setLoadingDocStatus(true);
    try {
      const status = await eventService.getEventDocumentStatus(raceId);
      setDocumentStatus(status);
      
      // Start polling if any document is processing
      if (isAnyDocumentProcessing(status)) {
        setPollingStatus(true);
      }
    } catch {
      setDocumentStatus(null);
    } finally {
      setLoadingDocStatus(false);
    }
  }, [isAnyDocumentProcessing]);

  /** Open the help modal for an event. */
  const handleAskAboutEvent = (event: RaceEvent) => {
    setHelpModalRaceId(event.raceId);
    setHelpModalEventName(event.name);
  };

  /** Format date for display (DD/MM/YYYY). */
  const formatDateDisplay = (dateStr?: string): string => {
    if (!dateStr) return '-';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  return (
    <div className="race-event-manager" data-testid="race-event-manager">
      <h2>Race Event Management</h2>

      {/* Success / Error messages */}
      {successMessage && (
        <div className="success-message" data-testid="success-message">
          {successMessage}
        </div>
      )}
      {error && (
        <div className="error-message" data-testid="error-message">
          {error}
        </div>
      )}

      {/* Create Event Button */}
      {!showCreateForm && !editingEvent && (
        <button
          type="button"
          onClick={() => setShowCreateForm(true)}
          className="create-event-btn"
          data-testid="show-create-form-btn"
        >
          + Create New Event
        </button>
      )}

      {/* Create / Edit Form */}
      {(showCreateForm || editingEvent) && (
        <form onSubmit={handleSubmit} data-testid="race-event-form">
          <h3>{editingEvent ? 'Edit Race Event' : 'Create Race Event'}</h3>

          <div className="form-field">
            <label htmlFor="event-name">Event Name:</label>
            <input
              id="event-name"
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="e.g., Sydney to Hobart 2026"
              data-testid="input-name"
            />
            {formErrors.name && (
              <span className="field-error" data-testid="error-name">{formErrors.name}</span>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="event-year">Year:</label>
            <input
              id="event-year"
              type="number"
              value={formYear}
              onChange={(e) => setFormYear(e.target.value)}
              placeholder="e.g., 2026"
              data-testid="input-year"
            />
            {formErrors.year && (
              <span className="field-error" data-testid="error-year">{formErrors.year}</span>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="event-date">Event Date:</label>
            <input
              id="event-date"
              type="date"
              value={formDate}
              onChange={(e) => setFormDate(e.target.value)}
              data-testid="input-date"
            />
            {formErrors.date && (
              <span className="field-error" data-testid="error-date">{formErrors.date}</span>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="event-category">Category:</label>
            <select
              id="event-category"
              value={formCategory}
              onChange={(e) => setFormCategory(e.target.value)}
              data-testid="input-category"
            >
              <option value="">Select a category</option>
              {categories.map((cat) => (
                <option key={cat.categoryId} value={cat.name}>
                  {cat.name}
                </option>
              ))}
            </select>
            {formErrors.category && (
              <span className="field-error" data-testid="error-category">{formErrors.category}</span>
            )}
          </div>

          <div className="form-actions">
            <button type="submit" disabled={submitting} data-testid="submit-btn">
              {submitting
                ? 'Saving…'
                : editingEvent
                ? 'Update Event'
                : 'Create Event'}
            </button>
            <button type="button" onClick={resetForm} data-testid="cancel-edit-btn">
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Race Events List */}
      <h3>Existing Race Events</h3>
      {loading && <p data-testid="loading-indicator">Loading…</p>}

      {!loading && events.length === 0 && (
        <p data-testid="no-events">No race events found.</p>
      )}

      {!loading && events.length > 0 && (
        <table className="race-events-table" data-testid="events-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Date</th>
              <th>Year</th>
              <th>Category</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {events.map((event) => (
              <tr key={event.raceId} data-testid={`event-row-${event.raceId}`}>
                <td>{event.name}</td>
                <td>{formatDateDisplay(event.date)}</td>
                <td>{event.year}</td>
                <td>{event.category}</td>
                <td className="action-buttons">
                  <button
                    onClick={() => handleEditClick(event)}
                    data-testid={`edit-btn-${event.raceId}`}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      if (uploadRaceId === event.raceId) {
                        // Toggle off if already showing this event's docs
                        setUploadRaceId(null);
                        setUploadFile(null);
                        setUploadError(null);
                        setUploadSuccess(null);
                        setDocumentStatus(null);
                        setPollingStatus(false);
                      } else {
                        // Show docs for this event
                        setUploadRaceId(event.raceId);
                        setUploadError(null);
                        setUploadSuccess(null);
                        setPollingStatus(false);
                        fetchDocumentStatus(event.raceId);
                      }
                    }}
                    data-testid={`upload-btn-${event.raceId}`}
                  >
                    Docs
                  </button>
                  <button
                    className="ask-event-btn"
                    onClick={() => handleAskAboutEvent(event)}
                    data-testid={`ask-btn-${event.raceId}`}
                  >
                    Ask
                  </button>
                  <button
                    className="delete-btn"
                    onClick={() => handleDeleteClick(event.raceId)}
                    data-testid={`delete-btn-${event.raceId}`}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="modal-overlay">
          <div className="modal-content delete-confirm-modal">
            <h3>Confirm Delete</h3>
            <p>
              Are you sure you want to delete the race event "
              {events.find((e) => e.raceId === deleteConfirmId)?.name}"?
            </p>
            <p className="warning-text">This action cannot be undone.</p>
            <div className="form-actions">
              <button
                className="delete-btn"
                onClick={handleDeleteConfirm}
                disabled={deleting}
                data-testid="confirm-delete-btn"
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
              <button
                onClick={handleDeleteCancel}
                disabled={deleting}
                data-testid="cancel-delete-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Upload Section */}
      {uploadRaceId && (
        <div className="document-upload-section" data-testid="document-upload-section">
          <h3>
            Upload Document for:{' '}
            {events.find((e) => e.raceId === uploadRaceId)?.name ?? uploadRaceId}
          </h3>

          {/* Document Status Display */}
          {loadingDocStatus && <p>Loading document status...</p>}
          {!loadingDocStatus && documentStatus && (
            <div className="event-document-status" data-testid="document-status">
              <h4>
                Document Status
                {pollingStatus && <span className="polling-indicator"> (auto-updating...)</span>}
              </h4>
              <div className="document-status-item">
                <span className="document-status-label">Notice of Race:</span>
                {documentStatus.noticeOfRace ? (
                  <>
                    <span className={`status-${documentStatus.noticeOfRace.extractionStatus}`}>
                      {documentStatus.noticeOfRace.extractionStatus}
                    </span>
                    {documentStatus.noticeOfRace.extractionStatus === 'complete' && (
                      <span className="section-count">
                        ({documentStatus.noticeOfRace.sectionCount} sections)
                      </span>
                    )}
                    <button
                      className="delete-doc-btn"
                      onClick={() => handleDeleteDocument('notice_of_race')}
                      disabled={deletingDocType === 'notice_of_race'}
                      title="Delete document"
                    >
                      {deletingDocType === 'notice_of_race' ? '...' : '🗑️'}
                    </button>
                  </>
                ) : (
                  <span className="status-not-uploaded">Not uploaded</span>
                )}
              </div>
              <div className="document-status-item">
                <span className="document-status-label">Sailing Instructions:</span>
                {documentStatus.sailingInstructions ? (
                  <>
                    <span className={`status-${documentStatus.sailingInstructions.extractionStatus}`}>
                      {documentStatus.sailingInstructions.extractionStatus}
                    </span>
                    {documentStatus.sailingInstructions.extractionStatus === 'complete' && (
                      <span className="section-count">
                        ({documentStatus.sailingInstructions.sectionCount} sections)
                      </span>
                    )}
                    <button
                      className="delete-doc-btn"
                      onClick={() => handleDeleteDocument('sailing_instructions')}
                      disabled={deletingDocType === 'sailing_instructions'}
                      title="Delete document"
                    >
                      {deletingDocType === 'sailing_instructions' ? '...' : '🗑️'}
                    </button>
                  </>
                ) : (
                  <span className="status-not-uploaded">Not uploaded</span>
                )}
              </div>
              {documentStatus.amendments.length > 0 && (
                <>
                  {documentStatus.amendments.map((amd) => (
                    <div key={amd.amendmentNumber} className="document-status-item">
                      <span className="document-status-label">Amendment {amd.amendmentNumber}:</span>
                      <span className={`status-${amd.extractionStatus}`}>
                        {amd.extractionStatus}
                      </span>
                      {amd.extractionStatus === 'complete' && (
                        <span className="section-count">({amd.sectionCount} sections)</span>
                      )}
                      <button
                        className="delete-doc-btn"
                        onClick={() => handleDeleteDocument(`amendment_${amd.amendmentNumber}`)}
                        disabled={deletingDocType === `amendment_${amd.amendmentNumber}`}
                        title="Delete document"
                      >
                        {deletingDocType === `amendment_${amd.amendmentNumber}` ? '...' : '🗑️'}
                      </button>
                    </div>
                  ))}
                </>
              )}

              {/* Extracted Requirements Display */}
              {documentStatus.noticeOfRace?.extractionStatus === 'complete' &&
                documentStatus.noticeOfRace?.extractedRequirements &&
                documentStatus.noticeOfRace.extractedRequirements.length > 0 && (
                <div className="extracted-requirements" data-testid="extracted-requirements">
                  <h4>Compliance Requirements (from NoR)</h4>
                  <table className="requirements-table">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Document Type</th>
                        <th>Description</th>
                        <th>Required</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documentStatus.noticeOfRace.extractedRequirements.map((req) => (
                        <tr key={req.requirementId}>
                          <td className={`category-${req.category}`}>
                            {req.category === 'crew' ? '👤 Crew' : '⛵ Boat'}
                          </td>
                          <td>{req.documentType.replace(/_/g, ' ')}</td>
                          <td className="requirement-description">{req.description}</td>
                          <td className="required-count">{req.requiredCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {uploadSuccess && (
            <div className="success-message" data-testid="upload-success-message">
              {uploadSuccess}
            </div>
          )}
          {uploadError && (
            <div className="error-message" data-testid="upload-error-message">
              {uploadError}
            </div>
          )}

          <form onSubmit={handleDocumentUpload} data-testid="document-upload-form">
            <div className="form-field">
              <label htmlFor="doc-type">Document Type:</label>
              <select
                id="doc-type"
                value={uploadDocType}
                onChange={(e) => setUploadDocType(e.target.value as EventDocumentType)}
                data-testid="input-doc-type"
              >
                <option value="notice_of_race">Notice of Race</option>
                <option value="sailing_instructions">Sailing Instructions</option>
                <option value="amendment">Amendment</option>
              </select>
            </div>

            <div className="form-field">
              <label htmlFor="doc-file">File (PDF):</label>
              <input
                id="doc-file"
                type="file"
                accept=".pdf"
                onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
                data-testid="input-file"
              />
            </div>

            <div className="form-actions">
              <button type="submit" disabled={uploading || !uploadFile} data-testid="upload-submit-btn">
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setUploadRaceId(null);
                  setUploadFile(null);
                  setUploadError(null);
                  setUploadSuccess(null);
                  setDocumentStatus(null);
                  setPollingStatus(false);
                }}
                data-testid="cancel-upload-btn"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Event Help Modal */}
      <EventHelpModal
        isOpen={helpModalRaceId !== null}
        onClose={() => {
          setHelpModalRaceId(null);
          setHelpModalEventName('');
        }}
        raceId={helpModalRaceId || ''}
        eventName={helpModalEventName}
      />
    </div>
  );
};

export default RaceEventManager;
